import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDatetime from 'react-datetime';
import _ from 'lodash';
import { useSnapshot } from 'valtio';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  Tooltip,
  TabPane,
} from 'reactstrap';
import Select from 'react-select';
import { optionizeAll, getYearMonth } from 'helpers/select';
import { useArrayStateObject } from 'components/CustomHooks';
import { getPortfolioOptimizerOptions } from 'services/analysis';
import { optimizationState } from '../state';
import { parseNumericValue } from './Shared';

function ModelingAssumptions({ optimizerOptions }) {
  const snap = useSnapshot(optimizationState);
  const currencyOption = useArrayStateObject({
    initialItems: [
      { value: 'Default', label: 'No' },
      { value: 'CAD', label: 'CAD (Convert USD)' },
      { value: 'USD', label: 'USD (Convert CAD)' },
    ],
  });
  const rebalanceFreqOption = useArrayStateObject({
    initialItems: optionizeAll(['Annual', 'Semi', 'Quarterly', 'Monthly']),
  });
  const confidenceOption = useArrayStateObject({
    initialItems: [
      { value: 0.9, label: '90%' },
      { value: 0.95, label: '95%' },
      { value: 0.99, label: '99%' },
    ],
  });
  const benchmarkOption = useArrayStateObject({ initialItems: [] });

  useEffect(() => {
    if (!optimizerOptions) return;
    const { benchmark_options } = optimizerOptions;

    const options = optionizeAll(benchmark_options);
    benchmarkOption.setItems(options);
    if (options.length > 0) benchmarkOption.setSelected(options[0]);
  }, [optimizerOptions]);

  useEffect(() => {
    optimizationState.currency = currencyOption.selected.value;
    optimizationState.rebalanceFreq = rebalanceFreqOption.selected.value;
    optimizationState.confidence = confidenceOption.selected.value;
  }, [currencyOption.selected, rebalanceFreqOption.selected, confidenceOption.selected]);

  useEffect(() => {
    if (!benchmarkOption.selected) return;
    optimizationState.benchmark = benchmarkOption.selected.value;
  }, [benchmarkOption.selected]);

  let sdate = null;
  if (snap.sdate) {
    const [month, year] = snap.sdate.split('/').map(Number);
    sdate = new Date(year, month - 1, 1);
  }

  return (
    <div className="tw-max-w-3xl tw-mx-auto">
      <Card>
        <CardHeader>
          <CardTitle tag="h3" className="text-center">
            Modeling Assumptions
          </CardTitle>
        </CardHeader>
        <CardBody className="mt-3">
          <Row>
            <Col sm="3" className="text-right">
              <Label>Convert Currency</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="currencyOption"
                  value={currencyOption.selected}
                  onChange={currencyOption.setSelected}
                  options={currencyOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Rebalance Freq</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="rebalanceFreqOption"
                  value={rebalanceFreqOption.selected}
                  onChange={rebalanceFreqOption.setSelected}
                  options={rebalanceFreqOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Confidence Interval</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="confidenceOption"
                  value={confidenceOption.selected}
                  onChange={confidenceOption.setSelected}
                  options={confidenceOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Benchmark</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <Select
                  className="react-select"
                  name="benchmarkOption"
                  value={benchmarkOption.selected}
                  onChange={benchmarkOption.setSelected}
                  options={benchmarkOption.items}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="3" className="text-right">
              <Label>Start Date (Optional)</Label>
            </Col>
            <Col sm="7">
              <FormGroup>
                <ReactDatetime
                  value={sdate}
                  dateFormat="MM/YYYY"
                  onChange={(date) => {
                    optimizationState.sdate = getYearMonth(date);
                  }}
                  inputProps={{
                    className: 'form-control',
                    placeholder: 'Start Date',
                  }}
                  closeOnSelect={true}
                  timeFormat={false}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
}

export default ModelingAssumptions;
